import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import ReviewToolbar from './ReviewToolbar';

const TableReview = () => {
  const [loading, setLoading] = useState(true)
  const [reload, setReload] = useState(0)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('uptime')
  const [selected, setSelected] = useState([])

  const loadReviews = 
    async (sort, query, column) => {
      try {
        const response = await apiService.get('/reviews', {
          params: {
            query,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;
        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    loadReviews(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadReviews(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('uptime')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadReviews(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      minWidth: 80,
      field: 'id',
      headerName: 'ID',
      renderCell: params => (
        <Link to={`/admin/review/${params.row.id}`} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.id}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </Link>
      )
    },
    {
      minWidth: 120,
      field: 'username',
      headerName: 'User ID',
      renderCell: params => (
        <Link to={`/admin/member/${params.row.username}`} >
          {params.row.username}
        </Link>
      )
    },
    {
      minWidth: 150,
      field: 'cat_id',
      headerName: 'CAT ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cat_id}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'sku',
      headerName: 'Product SKU',
      renderCell: params => (
        <Link to={`/admin/product/item/${params.row.sku}`} >
          {params.row.sku}
        </Link>
      )
    },
    {
      minWidth: 400,
      field: 'descript',
      headerName: '내용',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.descript}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'uptime',
      headerName: '변경날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uptime.toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='리뷰내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.id}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: ReviewToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableReview
